import React, { useState, useMemo } from "react";
import type {
  FamilyInformationData,
  SpouseInformation,
  ChildInformation,
  HomeAddressData,
} from "../../../types/customer-portal";
import { usePlanSelection } from "../../../contexts/PlanSelectionContext";
import SpouseInformationModal from "./SpouseInformationModal";
import ChildInformationModal from "./ChildInformationModal";
import IncompleteFamilyMembersModal from "./IncompleteFamilyMembersModal";
import { formatAddressForSummary } from "../../../utils/addressFormatter";
import { formatFullName } from "../../../utils/nameFormatter";
import "./FamilyInformationStep.css";

interface FamilyInformationStepProps {
  data?: FamilyInformationData;
  onNext: (data: FamilyInformationData) => void;
  onBack: () => void;
  principalHomeAddress?: HomeAddressData; // NEW: Principal Insured home address
}

const FamilyInformationStep: React.FC<FamilyInformationStepProps> = ({
  data,
  onNext,
  onBack,
  principalHomeAddress
}) => {
  // Get plan selection context for Family plan detection
  const { isFamilyPlan } = usePlanSelection();
  
  const [spouse, setSpouse] = useState<SpouseInformation | null>(data?.spouse || null);
  const [children, setChildren] = useState<ChildInformation[]>(data?.children || []);
  const [isSpouseModalOpen, setIsSpouseModalOpen] = useState(false);
  const [isChildModalOpen, setIsChildModalOpen] = useState(false);
  const [editingSpouse, setEditingSpouse] = useState<SpouseInformation | null>(null);
  const [editingChild, setEditingChild] = useState<ChildInformation | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // NEW: State for incomplete family members modal
  const [isIncompleteFamilyModalOpen, setIsIncompleteFamilyModalOpen] = useState(false);

  // NEW: Validation function for minimum 2 eligible dependents (Family plans only)
  const validateMinimumDependents = useMemo(() => {
    const currentIsFamilyPlan = isFamilyPlan();
    if (!currentIsFamilyPlan) {
      return true; // Skip validation for non-family plans
    }
    
    const dependentCount = (spouse ? 1 : 0) + children.length;
    return dependentCount >= 2;
  }, [spouse, children, isFamilyPlan]);

  // Form validation - keep original logic (at least 1 dependent) for button state
  // The minimum dependents validation will be checked in handleNext
  const isFormValid = useMemo(() => {
    const childrenHaveTitles = children.every((child) => !!child.title?.trim());
    if (!spouse) {
      return (spouse !== null || children.length > 0) && childrenHaveTitles;
    } else {
      return (
        children.length > 0 &&
        childrenHaveTitles &&
        !!spouse.title?.trim() &&
        !!spouse.idType &&
        !!spouse.idNumber
      );
    }
  }, [spouse, children]);

  const handleNext = () => {
    // NEW: Check minimum dependents validation for Family plans
    if (!validateMinimumDependents) {
      setIsIncompleteFamilyModalOpen(true);
      return;
    }
    
    if (isFormValid) {
      const familyData: FamilyInformationData = {
        familyMembers: [], // Keep for backward compatibility
        spouse: spouse || undefined,
        children: children
      };
      onNext(familyData);
    }
  };

  // Spouse handlers
  const handleAddSpouse = () => {
    setEditingSpouse(null);
    setIsEditMode(false);
    setIsSpouseModalOpen(true);
  };

  const handleEditSpouse = () => {
    if (spouse) {
      setEditingSpouse(spouse);
      setIsEditMode(true);
      setIsSpouseModalOpen(true);
    }
  };

  const handleSaveSpouse = (spouseData: SpouseInformation) => {
    setSpouse(spouseData);
    setIsSpouseModalOpen(false);
    setEditingSpouse(null);
    setIsEditMode(false);
  };

  const handleDeleteSpouse = () => {
    setSpouse(null);
    setIsSpouseModalOpen(false);
    setEditingSpouse(null);
    setIsEditMode(false);
  };

  // Child handlers
  const handleAddChild = () => {
    if (children.length >= 5) return; // Max 5 children
    setEditingChild(null);
    setIsEditMode(false);
    setIsChildModalOpen(true);
  };

  const handleEditChild = (child: ChildInformation) => {
    setEditingChild(child);
    setIsEditMode(true);
    setIsChildModalOpen(true);
  };

  const handleSaveChild = (childData: ChildInformation) => {
    if (isEditMode && editingChild) {
      // Update existing child
      setChildren(children.map(child => 
        child.id === editingChild.id ? childData : child
      ));
    } else {
      // Add new child
      setChildren([...children, childData]);
    }
    setIsChildModalOpen(false);
    setEditingChild(null);
    setIsEditMode(false);
  };

  const handleDeleteChild = (childId: string) => {
    setChildren(children.filter(child => child.id !== childId));
    setIsChildModalOpen(false);
    setEditingChild(null);
    setIsEditMode(false);
  };

  return (
    <>
      <div className="family-information-step-new">
        <div className="family-information-content-new">
          <div className="family-section-note">
            <h5>Important Note</h5>
            <p>
              Family plan applications that include legally adopted or
              non-legally adopted children require additional review before
              coverage can be issued. For assistance, please e-mail{" "}
              <a href="mailto:traveldirect_team@pacificcross.com.ph">
                traveldirect_team@pacificcross.com.ph
              </a>
              .
            </p>
          </div>
          {/* Spouse Information Section */}
          <div className="family-section-new">
            <div className="family-section-header-new">
              <div className="family-section-text-new">
                <h3 className="family-section-title-new">Spouse Information</h3>
                <p className="family-section-description-new">You have option to fill or not to fill this information</p>
              </div>
              {!spouse && (
                <button
                  type="button"
                  className="family-add-btn-new"
                  onClick={handleAddSpouse}
                  aria-label="Add spouse information"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>

            {/* Spouse Information Card */}
            {spouse && (
              <div className="family-member-card">
                <div className="family-member-header">
                  <div className="family-member-info">
                    <h4 className="family-member-name">
                      {formatFullName(spouse)}
                    </h4>
                    <p className="family-member-relationship">Spouse</p>
                    {!spouse.title?.trim() && (
                      <p className="text-error">
                        Title is required. Click edit to update.
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="family-edit-btn"
                    onClick={handleEditSpouse}
                    aria-label="Edit spouse information"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                <div className="family-member-details">
                  <div className="family-detail-row">
                    <span className="family-detail-label">Birthdate</span>
                    <span className="family-detail-value">{spouse.dateOfBirth}</span>
                  </div>
                  <div className="family-detail-row">
                    <span className="family-detail-label">Current Age</span>
                    <span className="family-detail-value">{spouse.age}</span>
                  </div>
                  <div className="family-detail-row">
                    <span className="family-detail-label">Gender</span>
                    <span className="family-detail-value">{spouse.gender}</span>
                  </div>
                  <div className="family-detail-row">
                    <span className="family-detail-label">Nationality</span>
                    <span className="family-detail-value">{spouse.nationality || 'Not specified'}</span>
                  </div>
                  <div className="family-detail-row">
                    <span className="family-detail-label">Address</span>
                    <span className="family-detail-value">
                      {formatAddressForSummary({
                        // houseUnitNo: spouse.houseUnitNo,
                        streetName: spouse.streetName,
                        subdivisionVillage: spouse.subdivisionVillage,
                        barangay: spouse.barangay,
                        city: spouse.city,
                        province: spouse.province,
                        zipCode: spouse.zipCode,
                        country: spouse.country
                      })}
                    </span>
                  </div>
                  <div className="family-detail-row">
                    <span className="family-detail-label">Type of ID</span>
                    <span className={`family-detail-value ${!spouse.idType?"text-error":""}`}>{spouse.idType || 'Required field'}</span>
                  </div>
                  <div className="family-detail-row">
                    <span className="family-detail-label">ID Number</span>
                    <span className={`family-detail-value ${!spouse.idNumber?"text-error":""}`}>{spouse.idNumber || 'Required field'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Child Information Section */}
          <div className="family-section-new">
            <div className="family-section-header-new">
              <div className="family-section-text-new">
                <h3 className="family-section-title-new">Child Information</h3>
                <p className="family-section-description-new">You can add up to 5 children</p>
              </div>
              {children.length < 5 && (
                <button
                  type="button"
                  className="family-add-btn-new"
                  onClick={handleAddChild}
                  aria-label="Add child information"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>

            {/* Children Information Cards */}
            {children.map((child) => (
              <div key={child.id} className="family-member-card">
                <div className="family-member-header">
                  <div className="family-member-info">
                    <h4 className="family-member-name">
                      {formatFullName(child)}
                    </h4>
                    <p className="family-member-relationship">Child</p>
                    {!child.title?.trim() && (
                      <p className="text-error">
                        Title is required. Click edit to update.
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="family-edit-btn"
                    onClick={() => handleEditChild(child)}
                    aria-label="Edit child information"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                <div className="family-member-details">
                  <div className="family-detail-row">
                    <span className="family-detail-label">Birthdate</span>
                    <span className="family-detail-value">{child.dateOfBirth}</span>
                  </div>
                  <div className="family-detail-row">
                    <span className="family-detail-label">Current Age</span>
                    <span className="family-detail-value">{child.age}</span>
                  </div>
                  <div className="family-detail-row">
                    <span className="family-detail-label">Gender</span>
                    <span className="family-detail-value">{child.gender}</span>
                  </div>
                  {/* <div className="family-detail-row">
                    <span className="family-detail-label">Nationality</span>
                    <span className="family-detail-value">Filipino</span>
                  </div> */}
                  <div className="family-detail-row">
                    <span className="family-detail-label">Address</span>
                    <span className="family-detail-value">
                      {formatAddressForSummary({
                        // houseUnitNo: child.houseUnitNo,
                        streetName: child.streetName,
                        subdivisionVillage: child.subdivisionVillage,
                        barangay: child.barangay,
                        city: child.city,
                        province: child.province,
                        zipCode: child.zipCode,
                        country: child.country
                      })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="step-navigation-new">
            <button
              type="button"
              className="btn-back-new"
              onClick={onBack}
            >
              Back
            </button>
            <button
              type="button"
              className={`btn-next-new ${!isFormValid ? 'disabled' : ''}`}
              onClick={handleNext}
              disabled={!isFormValid}
            >
              Next
            </button>
          </div>

          {/* Footer */}
          <span className='assistance-footer'>
            Need assistance? Email us at  <span className="email-text">traveldirect_team@pacificcross.com.ph</span> 
          </span>          
        </div>
      </div>

      {/* Modals */}
      <SpouseInformationModal
        isOpen={isSpouseModalOpen}
        onClose={() => {
          setIsSpouseModalOpen(false);
          setEditingSpouse(null);
          setIsEditMode(false);
        }}
        onSave={handleSaveSpouse}
        onDelete={handleDeleteSpouse}
        spouse={editingSpouse}
        isEditMode={isEditMode}
        principalHomeAddress={principalHomeAddress}
      />

      <ChildInformationModal
        isOpen={isChildModalOpen}
        onClose={() => {
          setIsChildModalOpen(false);
          setEditingChild(null);
          setIsEditMode(false);
        }}
        onSave={handleSaveChild}
        onDelete={handleDeleteChild}
        child={editingChild}
        isEditMode={isEditMode}
        spouseAddress={spouse}
        principalHomeAddress={principalHomeAddress}
      />

      {/* NEW: Incomplete Family Members Modal */}
      <IncompleteFamilyMembersModal
        isOpen={isIncompleteFamilyModalOpen}
        onClose={() => setIsIncompleteFamilyModalOpen(false)}
      />
    </>
  );
};

export default FamilyInformationStep;